/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {GetTransmissionOptionsRequest} from '../models/get-transmission-options-request';
import {UpdateTransmissionAttributeRequest} from '../models/update-transmission-attribute-request';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})

export class ManageTransmissionAttributeService {

  private readonly baseUrl: string;

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
    this.baseUrl = `${this.configService.baseApiUrl}manage-attributes/transmission/`;
  }

  fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  fileExtension = '.xlsx';

  /**
   * Get all default options for the dropdowns.
   */
  getAllOptions(): Observable<any> {
    return this.http.get(this.baseUrl + `get-all-options`)
    .pipe(catchError(myError => this.handleError(myError, 'Unable to initialize dropdown values')));
  }

  /**
   * Get all cascading dropdown options.
   * @param req user selected params.
   */
  getFilteredOptions(req: GetTransmissionOptionsRequest): Observable<any> {
    return this.http.post(this.baseUrl + `get-filtered-options`, req)
    .pipe(catchError(myError => this.handleError(myError, 'Unable to fetch filtered dropdown values')));
  }

  /**
   * Get preview/ commit data as blob data object.
   * @param req Users selection with old and new value.
   * @param fileName name of the file that will be saved as excel.
   */
  updateAttribute(req: UpdateTransmissionAttributeRequest, fileName: string): Observable<any> {
    const headers = new HttpHeaders();
    headers['Content-disposition'] = 'attachment; filename=' + fileName + '.xlsx';
    return this.http.post(this.baseUrl + `update-transmission-attribute`, req, {headers, responseType: 'blob'})
    .pipe(catchError(myError => this.handleError(myError, 'Unable to ' + (req.doCommit ? 'commit ' : 'preview ') + 'changes')));
  }

  private handleError(errorResponse: HttpErrorResponse, message: string): any {
    let errorMessage = message;
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
